import { useState } from 'react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowsDownUpIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { WorkbookColumnarSheet } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { IconTooltip } from '@/components/common/IconTooltip';
import { WorksheetReorderHandle } from './WorksheetReorderHandle';
import { useReorderSensors } from './useReorderSensors';

/**
 * Rearrange columns: a dialog listing the Worksheet's columns to reorder all
 * at once (drag, or arrow keys on a column's handle). Apply hands `rearrange`
 * the new order, as the columns' current indexes, so it lands as one edit.
 */
export function WorksheetColumnOrder({
  sheet,
  rearrange,
}: {
  sheet: WorkbookColumnarSheet;
  rearrange: (order: number[]) => void;
}) {
  const gt = useGT();
  const [order, setOrder] = useState<number[] | null>(null);
  const label = gt('Rearrange columns', {
    context: 'worksheet rearrange columns',
  });
  return (
    <>
      <IconTooltip>
        <Button
          variant="ghost"
          size="sm"
          aria-label={label}
          disabled={sheet.columns.length < 2}
          onClick={() => setOrder(sheet.columns.map((_, index) => index))}
        >
          <ArrowsDownUpIcon />
        </Button>
      </IconTooltip>
      <Dialog
        open={order !== null}
        onOpenChange={(open) => {
          if (!open) setOrder(null);
        }}
      >
        <DialogContent className="max-h-[80vh] grid-rows-[auto_minmax(0,1fr)_auto]">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              <T context="worksheet rearrange columns description">
                Drag columns into the order you want, first to last, then apply.
              </T>
            </DialogDescription>
          </DialogHeader>
          {order && (
            <ColumnOrderList sheet={sheet} order={order} setOrder={setOrder} />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOrder(null)}>
              <T context="worksheet rearrange columns cancel">Cancel</T>
            </Button>
            <Button
              onClick={() => {
                if (order) rearrange(order);
                setOrder(null);
              }}
            >
              <T context="worksheet rearrange columns apply">Apply</T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ColumnOrderList({
  sheet,
  order,
  setOrder,
}: {
  sheet: WorkbookColumnarSheet;
  order: number[];
  setOrder: (order: number[]) => void;
}) {
  const gt = useGT();
  const [moved, setMoved] = useState('');
  const sensors = useReorderSensors();
  function move(from: number, to: number) {
    const column = sheet.columns[order[from] ?? -1];
    if (!column || to < 0 || to >= order.length) return;
    setOrder(arrayMove(order, from, to));
    setMoved(
      gt('Moved {column} to position {position}', {
        context: 'worksheet rearrange columns, for screen readers',
        column: column.name,
        position: String(to + 1),
      }),
    );
  }
  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const ids = order.map(String);
    move(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
  }
  return (
    <div className="min-h-0 overflow-y-auto">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        {/* Ids are the columns' current indexes, so even duplicate names stay apart. */}
        <SortableContext
          items={order.map(String)}
          strategy={verticalListSortingStrategy}
        >
          <ol
            className="space-y-1"
            aria-label={gt('Columns', {
              context: 'worksheet rearrange columns list',
            })}
          >
            {order.map((from, index) => (
              <ColumnOrderRow
                key={from}
                id={String(from)}
                name={sheet.columns[from]?.name ?? ''}
                index={index}
                move={move}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
      <p role="status" className="sr-only">
        {moved}
      </p>
    </div>
  );
}

function ColumnOrderRow({
  id,
  name,
  index,
  move,
}: {
  id: string;
  name: string;
  index: number;
  move: (from: number, to: number) => void;
}) {
  const gt = useGT();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex items-center gap-2 rounded border bg-background p-1 text-sm"
    >
      <WorksheetReorderHandle
        sortable={{ attributes, listeners }}
        label={gt('Reorder {column}. Use the up and down arrow keys.', {
          context: 'worksheet rearrange columns drag handle',
          column: name,
        })}
        move={(delta) => move(index, index + delta)}
      />
      <span className="w-5 text-right text-xs text-muted-foreground tabular-nums">
        {index + 1}
      </span>
      <span className="min-w-0 flex-1 truncate" title={name}>
        {name}
      </span>
    </li>
  );
}
